import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Loader2, AlertTriangle, RefreshCw, TrendingDown, TrendingUp } from "lucide-react";
import { toast } from "sonner";
import { Slider } from "@/components/ui/slider";

const STATUS_LABELS = {
  pending: "En attente",
  in_progress: "En cours",
  completed: "Terminé",
  paused: "En pause",
  cancelled: "Annulé",
  ready: "Prête",
  cashed: "Encaissé",
};

/**
 * Onglet Analytics : liste les chantiers des N derniers jours où la conso
 * réelle a divergé de plus de X% du prévu. X est configurable dynamiquement
 * via un slider, avec une valeur initiale lue dans app-config.
 */
export default function ForecastActualVarianceTab({ defaultThreshold = 10 }) {
  const [days, setDays] = useState(30);
  const [threshold, setThreshold] = useState(defaultThreshold);
  const [data, setData] = useState({ items: [], count: 0 });
  const [loading, setLoading] = useState(false);
  const [expandedOrderId, setExpandedOrderId] = useState(null);
  const [initialized, setInitialized] = useState(false);

  // Init threshold from defaultThreshold (once when it stabilizes — avoids race-fetch with undefined)
  useEffect(() => {
    if (!initialized && defaultThreshold != null && !Number.isNaN(parseFloat(defaultThreshold))) {
      setThreshold(parseFloat(defaultThreshold));
      setInitialized(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultThreshold]);

  const fetchData = useCallback(async () => {
    if (threshold == null || Number.isNaN(threshold)) return;
    try {
      setLoading(true);
      const res = await api.getForecastActualDivergence({ days, threshold_pct: threshold });
      setData(res.data || { items: [], count: 0 });
    } catch (e) {
      // Silent on initial mount race (avoid false positive on tab switch)
      console.error("[ForecastActualVarianceTab] fetch error:", e);
      toast.error("Erreur de chargement des écarts");
    } finally {
      setLoading(false);
    }
  }, [days, threshold]);

  useEffect(() => { fetchData(); }, [fetchData]);

  return (
    <div className="space-y-4" data-testid="forecast-variance-tab">
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="font-heading flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-amber-600" />
            Écarts Prévu / Réel sur les matières
          </CardTitle>
          <p className="text-xs text-muted-foreground mt-1">
            Liste les chantiers où la consommation réelle de matières a divergé du prévu au-delà du seuil défini.
            Utilise les lignes archivées du forecast (conso réelle saisie).
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-4 items-end">
            <div className="md:col-span-3">
              <Label className="text-xs">Période (jours)</Label>
              <Input
                type="number"
                min={1} max={365}
                value={days}
                onChange={(e) => setDays(Math.max(1, parseInt(e.target.value, 10) || 30))}
                data-testid="variance-days-input"
              />
            </div>
            <div className="md:col-span-7">
              <Label className="text-xs">Seuil d&apos;écart : <strong>{threshold}%</strong></Label>
              <Slider
                value={[threshold]}
                min={0} max={100} step={1}
                onValueChange={(v) => setThreshold(v[0])}
                className="mt-3"
                data-testid="variance-threshold-slider"
              />
            </div>
            <div className="md:col-span-2 flex justify-end">
              <Button
                size="sm" variant="outline" onClick={fetchData} disabled={loading}
                data-testid="variance-refresh"
              >
                {loading ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-1" />}
                Rafraîchir
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      ) : data.items.length === 0 ? (
        <Card>
          <CardContent className="pt-6 pb-6 text-center text-sm text-muted-foreground italic">
            Aucun chantier avec écart supérieur à {threshold}% sur les {days} derniers jours.
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Commande</TableHead>
                  <TableHead>Client</TableHead>
                  <TableHead>Statut</TableHead>
                  <TableHead className="text-right">Matières</TableHead>
                  <TableHead className="text-right">Écart max</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.items.map((it) => {
                  const isOpen = expandedOrderId === it.order_id;
                  const positive = it.materials.some((m) => m.variance_pct > 0);
                  const negative = it.materials.some((m) => m.variance_pct < 0);
                  return [
                    <TableRow
                      key={`row-${it.order_id}`}
                      data-testid={`variance-row-${it.order_id}`}
                      className={Math.abs(it.max_variance_pct) >= 20 ? "bg-red-50/60" : ""}
                    >
                      <TableCell className="font-mono text-xs">{it.order_reference || it.order_id.slice(0, 8)}</TableCell>
                      <TableCell className="text-sm">{it.client_name || "—"}</TableCell>
                      <TableCell>
                        <Badge variant="outline" className="text-[10px]">{STATUS_LABELS[it.order_status] || it.order_status}</Badge>
                      </TableCell>
                      <TableCell className="text-right text-sm">
                        {it.materials_with_variance}
                      </TableCell>
                      <TableCell className="text-right">
                        <span
                          className={`font-bold font-mono ${
                            Math.abs(it.max_variance_pct) >= 20 ? "text-red-700"
                              : Math.abs(it.max_variance_pct) >= 10 ? "text-amber-700"
                              : "text-emerald-700"
                          }`}
                        >
                          {it.max_variance_pct > 0 ? "+" : ""}{it.max_variance_pct}%
                        </span>
                        {positive && <TrendingUp className="inline h-3 w-3 ml-1 text-red-500" />}
                        {negative && <TrendingDown className="inline h-3 w-3 ml-1 text-emerald-500" />}
                      </TableCell>
                      <TableCell>
                        <Button
                          variant="ghost" size="sm"
                          onClick={() => setExpandedOrderId(isOpen ? null : it.order_id)}
                          data-testid={`variance-toggle-${it.order_id}`}
                        >
                          {isOpen ? "Masquer" : "Détail"}
                        </Button>
                      </TableCell>
                    </TableRow>,
                    isOpen && (
                      <TableRow key={`details-${it.order_id}`} className="bg-slate-50">
                        <TableCell colSpan={6} className="py-3">
                          <Table className="bg-white border rounded">
                            <TableHeader>
                              <TableRow>
                                <TableHead className="text-xs">Produit</TableHead>
                                <TableHead className="text-xs text-right">Prévu</TableHead>
                                <TableHead className="text-xs text-right">Réel</TableHead>
                                <TableHead className="text-xs text-right">Δ</TableHead>
                                <TableHead className="text-xs text-right">Écart</TableHead>
                                <TableHead className="text-xs">Saisi le</TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {it.materials.map((m) => (
                                <TableRow key={m.material_id} data-testid={`variance-mat-${m.material_id}`}>
                                  <TableCell className="text-xs">{m.product_name || "—"}</TableCell>
                                  <TableCell className="text-xs text-right font-mono">{m.planned_quantity} {m.reference_unit || ""}</TableCell>
                                  <TableCell className="text-xs text-right font-mono">{m.actual_quantity} {m.reference_unit || ""}</TableCell>
                                  <TableCell className={`text-xs text-right font-mono ${m.delta > 0 ? "text-red-700" : "text-emerald-700"}`}>
                                    {m.delta > 0 ? "+" : ""}{m.delta}
                                  </TableCell>
                                  <TableCell className={`text-xs text-right font-bold ${
                                    Math.abs(m.variance_pct) >= 20 ? "text-red-700"
                                      : Math.abs(m.variance_pct) >= 10 ? "text-amber-700"
                                      : "text-emerald-700"
                                  }`}>
                                    {m.variance_pct > 0 ? "+" : ""}{m.variance_pct}%
                                  </TableCell>
                                  <TableCell className="text-xs text-muted-foreground">
                                    {m.actual_recorded_at ? new Date(m.actual_recorded_at).toLocaleDateString("fr-FR") : "—"}
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableCell>
                      </TableRow>
                    ),
                  ];
                }).flat().filter(Boolean)}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
